// Scan the QR on the driver's PO copy (or type the PO number) → the order page, where the guard
// sees where to send the truck and records this consignment. Phone-first: the camera opens as soon as the tab is shown, in a big square
// viewfinder, with a torch toggle for night duty where the phone supports it. The QR is read on
// the device when the browser has a BarcodeDetector; otherwise the shutter button sends one
// frame to the server to decode. A QR opened with the phone's own camera lands here as
// /scan?t=<token> and resolves straight away.

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Camera, Flashlight, FlashlightOff, Keyboard, Loader2, ScanQrCode, Search } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { gateApi } from "@/lib/gate";
import { playScanSound, unlockScanSounds } from "@/lib/scan-sounds";
import { useGate } from "@/lib/session-scope";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface NativeBarcodeDetector {
  detect(source: CanvasImageSource): Promise<{ rawValue?: string }[]>;
}
interface NativeBarcodeDetectorCtor {
  new (options?: { formats: string[] }): NativeBarcodeDetector;
  getSupportedFormats(): Promise<string[]>;
}

/** A QR detector when the browser has one, else null (→ shutter button + server decode). */
async function createQrDetector(): Promise<NativeBarcodeDetector | null> {
  const Ctor = (globalThis as unknown as { BarcodeDetector?: NativeBarcodeDetectorCtor }).BarcodeDetector;
  if (!Ctor) return null;
  try {
    const formats = await Ctor.getSupportedFormats();
    return formats.includes("qr_code") ? new Ctor({ formats: ["qr_code"] }) : null;
  } catch {
    return null;
  }
}

const DETECT_INTERVAL_MS = 300;
const FRAME_MAX_WIDTH = 900;

type CameraState = "off" | "starting" | "on" | "denied";

function msg(e: unknown) {
  return e instanceof ApiError ? e.message : "Something went wrong.";
}

export default function Scan() {
  const { token } = useAuth();
  const { gate } = useGate();
  const [params, setParams] = useSearchParams();

  const navigate = useNavigate();
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [camera, setCamera] = useState<CameraState>("off");
  const [autoDetect, setAutoDetect] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const busyRef = useRef(false);
  const detectorRef = useRef<NativeBarcodeDetector | null>(null);
  const detectingRef = useRef(false);
  const armedRef = useRef(true);
  const missesRef = useRef(0);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCamera((c) => (c === "denied" ? c : "off"));
    setTorchOn(false);
    setTorchAvailable(false);
  }, []);

  const startCamera = useCallback(async () => {
    if (streamRef.current) return;
    setCamera("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      const track = stream.getVideoTracks()[0];
      const caps = (track?.getCapabilities?.() ?? {}) as MediaTrackCapabilities & { torch?: boolean };
      setTorchAvailable(!!caps.torch);
      setCamera("on"); // the effect below attaches the stream once the video element is rendered
    } catch {
      setCamera("denied");
    }
  }, []);

  const resolveScan = useCallback(
    async (scanned: string, quiet: boolean) => {
      if (!scanned.trim()) return;
      setBusy(true);
      try {
        const d = await gateApi.resolve(token, gate.id, scanned.trim());
        if (d?.errorMessage) {
          if (!quiet) {
            playScanSound("fail");
            toast.error(d.errorMessage);
          }
          return;
        }
        playScanSound("success");
        navigator.vibrate?.(40);
        stopCamera();
        // The order: where to send the truck — and, where the store notifies on scan, who was just told.
        navigate(`/order/${d.id}`, { state: { arrival: d.arrival ?? null, arrivalError: d.arrivalError ?? null } });
      } catch (e) {
        if (!quiet) toast.error(msg(e));
      } finally {
        setBusy(false);
      }
    },
    [token, gate.id, stopCamera, navigate],
  );

  // QR opened with the phone camera: /scan?t=<token>. Resolve once, then drop the token from the URL.
  const deepLink = params.get("t");
  useEffect(() => {
    if (!deepLink) return;
    void resolveScan(deepLink, false);
    setParams({}, { replace: true });
  }, [deepLink, resolveScan, setParams]);

  // The Scan tab is for scanning: open the camera straight away (not when arriving via a deep link).
  useEffect(() => {
    if (!deepLink) void startCamera();
    return stopCamera;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per visit
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (camera !== "on" || !video || !streamRef.current || video.srcObject === streamRef.current) return;
    video.srcObject = streamRef.current;
    void video.play().catch(() => {});
  }, [camera]);

  async function toggleTorch() {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn } as MediaTrackConstraintSet] });
      setTorchOn((v) => !v);
    } catch {
      setTorchAvailable(false);
    }
  }

  /** One frame to the server for decoding (browsers without a QR detector, e.g. iPhone Safari). */
  const scanOnce = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || busyRef.current || video.videoWidth === 0) return;
    unlockScanSounds();
    busyRef.current = true;
    setBusy(true);
    try {
      const scale = Math.min(1, FRAME_MAX_WIDTH / video.videoWidth);
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
      const d = await gateApi.decodeQr(token, gate.id, canvas.toDataURL("image/jpeg", 0.8));
      if (!d?.text) {
        playScanSound("fail");
        toast.error("No QR found — hold the gate pass inside the square and try again.");
        return;
      }
      await resolveScan(d.text, false);
    } catch (e) {
      toast.error(msg(e));
    } finally {
      armedRef.current = false;
      missesRef.current = 0;
      busyRef.current = false;
      setBusy(false);
    }
  }, [token, gate.id, resolveScan]);

  useEffect(() => {
    let cancelled = false;
    void createQrDetector().then((d) => {
      if (cancelled) return;
      detectorRef.current = d;
      setAutoDetect(!!d);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-detect loop: the browser's own detector reads the QR locally when it can.
  useEffect(() => {
    if (camera !== "on") return;
    armedRef.current = true;
    missesRef.current = 0;
    const timer = setInterval(() => {
      const video = videoRef.current;
      const detector = detectorRef.current;
      if (!video || !detector || busyRef.current || detectingRef.current || video.videoWidth === 0) return;
      detectingRef.current = true;
      detector
        .detect(video)
        .then((found) => {
          if (!found.length) {
            missesRef.current += 1;
            if (missesRef.current >= 2) armedRef.current = true;
            return;
          }
          missesRef.current = 0;
          const text = found[0]?.rawValue;
          if (armedRef.current && text) {
            armedRef.current = false;
            void resolveScan(text, true);
          }
        })
        .catch(() => {})
        .finally(() => {
          detectingRef.current = false;
        });
    }, DETECT_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [camera, resolveScan]);

  return (
    // Phone: viewfinder, then the manual entry. Tablet / desktop: viewfinder left, manual entry +
    // how-to right.
    <div className="flex w-full flex-col gap-4 px-4 pt-4 pb-8 md:grid md:grid-cols-[minmax(0,28rem)_minmax(0,1fr)] md:items-start md:gap-6 md:px-6">
      <div className="flex flex-col gap-4">
        {/* Viewfinder. */}
        <div className="relative aspect-square w-full overflow-hidden rounded-3xl bg-black shadow-lg">
          <video
            ref={videoRef}
            playsInline
            muted
            className={cn("size-full object-cover", camera !== "on" && "invisible")}
          />
          <canvas ref={canvasRef} hidden />

          {camera === "on" ? (
            <>
              {/* Corner brackets. */}
              <div className="pointer-events-none absolute inset-[14%]">
                {[
                  "top-0 left-0 border-t-4 border-l-4 rounded-tl-2xl",
                  "top-0 right-0 border-t-4 border-r-4 rounded-tr-2xl",
                  "bottom-0 left-0 border-b-4 border-l-4 rounded-bl-2xl",
                  "bottom-0 right-0 border-b-4 border-r-4 rounded-br-2xl",
                ].map((c) => (
                  <span key={c} className={cn("absolute size-10 border-white", c)} />
                ))}
              </div>
              <p className="absolute inset-x-0 bottom-3 text-center text-sm font-medium text-white drop-shadow">
                {busy
                  ? "Looking up…"
                  : autoDetect
                    ? "Point at the QR on the driver’s copy"
                    : "Fit the QR in the square, then tap Capture"}
              </p>
              {torchAvailable && (
                <Button
                  variant="secondary"
                  aria-label={torchOn ? "Torch off" : "Torch on"}
                  className="absolute top-3 right-3 size-11 rounded-full bg-black/50 text-white hover:bg-black/70"
                  onClick={() => void toggleTorch()}
                >
                  {torchOn ? <FlashlightOff className="size-5" /> : <Flashlight className="size-5" />}
                </Button>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center text-white">
              {camera === "starting" ? (
                <Loader2 className="size-8 animate-spin" />
              ) : (
                <>
                  <Camera className="size-10 opacity-70" />
                  <p className="text-sm opacity-80">
                    {camera === "denied"
                      ? "The camera is blocked. Allow camera access in the browser, or type the PO number below."
                      : "Camera is off."}
                  </p>
                  <Button
                    className="h-12 rounded-xl px-6 text-base"
                    onClick={() => {
                      unlockScanSounds();
                      setCamera("off");
                      void startCamera();
                    }}
                  >
                    <Camera className="size-5" /> Open camera
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {camera === "on" && !autoDetect && (
          <Button className="h-14 rounded-2xl text-base shadow-md" onClick={() => void scanOnce()} disabled={busy}>
            {busy ? <Loader2 className="size-5 animate-spin" /> : <ScanQrCode className="size-5" />} Capture
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-4">
        {/* Manual fallback: crumpled / missing printout. */}
        <div className="flex flex-col gap-2 rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Keyboard className="size-4 text-muted-foreground" /> No QR? Type the PO number
          </div>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              unlockScanSounds();
              void resolveScan(manual, false);
            }}
          >
            <Input
              value={manual}
              onChange={(e) => setManual(e.target.value.toUpperCase())}
              placeholder="PO number"
              autoCapitalize="characters"
              autoComplete="off"
              enterKeyHint="search"
              className="h-12 rounded-xl font-mono text-base md:text-base"
            />
            <Button type="submit" className="h-12 shrink-0 rounded-xl px-4" disabled={busy || !manual.trim()}>
              {busy ? <Loader2 className="animate-spin" /> : <Search />} Find
            </Button>
          </form>
          <p className="text-xs text-muted-foreground">Or pick it from Expected deliveries.</p>
        </div>

        {/* How-to — fills the side on wide screens; on a phone the viewfinder says enough. */}
        <div className="hidden flex-col gap-3 rounded-2xl border border-border bg-card p-4 md:flex">
          <div className="text-sm font-semibold">At the gate</div>
          <ol className="flex flex-col gap-3 text-sm">
            {[
              ["Scan", "Hold the QR on the driver’s copy of the purchase order inside the square."],
              [
                "Direct",
                "The order opens: which department and site the truck must go to, and its earlier consignments.",
              ],
              [
                "Record",
                "Vehicle, driver and the challan (number or photo), then Record consignment — the department and the store are emailed to come and receive it.",
              ],
            ].map(([title, text], i) => (
              <li key={title} className="flex gap-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                  {i + 1}
                </span>
                <span>
                  <span className="font-medium">{title}</span>
                  <span className="block text-muted-foreground">{text}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
